/**
 * A FIFO concurrency limiter: at most `max` tasks run at once, the rest queue in call order.
 * Create it at module level so every list on screen shares the one budget.
 */
export function createLimiter(max: number) {
  let active = 0;
  const queue: (() => void)[] = [];
  const next = () => {
    if (active >= max) return;
    const run = queue.shift();
    if (!run) return;
    active += 1;
    run();
  };
  return <T>(task: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      queue.push(() => {
        task()
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            next();
          });
      });
      next();
    });
}

/**
 * Per-row run reads (`GET catalog-runs/{id}` + `…/progress`, ~10–20 serial queries each and a
 * Temporal query for a live run): a product's history or a list of run-name links never opens
 * more than this many at once, so one page cannot exhaust catalog-service's DB pool.
 */
const RUN_ROW_READ_CONCURRENCY = 4;

/** The shared limiter for per-row run reads — the fallback path when `catalog-runs/summaries` is missing. */
export const limitRunRowRead = createLimiter(RUN_ROW_READ_CONCURRENCY);
